/**
 * Analytical Tree - Controls Panel
 * Root type and entity selection; As of now/date; filters.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Slider,
  Radio,
  RadioGroup,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import { AccountTree } from '@mui/icons-material';
import axios from 'axios';

const ROOT_TYPES = [
  { value: 'aircraft', label: 'Aircraft' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'component', label: 'Component' },
  { value: 'charter', label: 'Charter / Flight' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'amp', label: 'AMP' },
  { value: 'adsb', label: 'AD/SB' },
  { value: 'techlog', label: 'Technical LogBook' }
];

const INCLUDE_TYPES = [
  { value: 'aircraft', label: 'Aircraft' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'component', label: 'Component' },
  { value: 'charter', label: 'Charter' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'amp', label: 'AMP' },
  { value: 'adsb', label: 'AD/SB' },
  { value: 'techlog', label: 'Tech Log' }
];

const ControlsPanel = ({ onLoad, loading }) => {
  const [rootType, setRootType] = useState('aircraft');
  const [rootId, setRootId] = useState('');
  const [asOfMode, setAsOfMode] = useState('now');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [maxDepth, setMaxDepth] = useState(3);
  const [includeTypes, setIncludeTypes] = useState(INCLUDE_TYPES.map((t) => t.value));
  const [onlyWithAnomalies, setOnlyWithAnomalies] = useState(false);

  const [aircraftList, setAircraftList] = useState([]);
  const [assemblyList, setAssemblyList] = useState([]);
  const [componentList, setComponentList] = useState([]);
  const [maintenanceList, setMaintenanceList] = useState([]);
  const [ampList, setAmpList] = useState([]);
  const [adsbList, setAdsbList] = useState([]);
  const [techlogList, setTechlogList] = useState([]);
  const [selectedAircraftId, setSelectedAircraftId] = useState('');
  const [selectedAssemblyId, setSelectedAssemblyId] = useState('');
  const [entityOptionsLoading, setEntityOptionsLoading] = useState(false);

  // Fetch aircraft list once
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const fetchAircraft = async () => {
      try {
        const res = await axios.get('/api/aircraft', { headers: { Authorization: `Bearer ${token}` } });
        setAircraftList(res.data || []);
      } catch (err) {
        console.error('Failed to fetch aircraft options:', err);
        setAircraftList([]);
      }
    };

    fetchAircraft();
  }, []);

  // Reset entity selections when root type changes
  useEffect(() => {
    setRootId('');
    setSelectedAircraftId('');
    setSelectedAssemblyId('');
    setAssemblyList([]);
    setComponentList([]);
    setMaintenanceList([]);
    setAmpList([]);
    setAdsbList([]);
    setTechlogList([]);
  }, [rootType]);

  // Fetch dependent lists based on selected aircraft/assembly and root type
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const fetchData = async () => {
      if (!selectedAircraftId) {
        setAssemblyList([]);
        setComponentList([]);
        setMaintenanceList([]);
        setAmpList([]);
        setAdsbList([]);
        setTechlogList([]);
        return;
      }

      setEntityOptionsLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };

        if (rootType === 'assembly' || rootType === 'component') {
          const res = await axios.get(`/api/assemblies?aircraftId=${selectedAircraftId}&isActive=true`, { headers });
          setAssemblyList(res.data || []);
        }

        if (rootType === 'component' && selectedAssemblyId) {
          const res = await axios.get(
            `/api/components?aircraftId=${selectedAircraftId}&assemblyId=${selectedAssemblyId}&isActive=true`,
            { headers }
          );
          setComponentList(res.data || []);
        }

        if (rootType === 'maintenance') {
          const res = await axios.get(`/api/maintenance?aircraftId=${selectedAircraftId}`, { headers });
          setMaintenanceList(res.data || []);
        }

        if (rootType === 'amp') {
          const res = await axios.get(`/api/amp-schedules?aircraftId=${selectedAircraftId}`, { headers });
          setAmpList(res.data || []);
        }

        if (rootType === 'adsb') {
          const res = await axios.get(`/api/adsb-service-bulletins?aircraftId=${selectedAircraftId}`, { headers });
          setAdsbList(res.data || []);
        }

        if (rootType === 'techlog') {
          const res = await axios.get(`/api/technical-log-book?aircraftId=${selectedAircraftId}`, { headers });
          setTechlogList(res.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch dependent options:', err);
        if (rootType === 'assembly' || rootType === 'component') {
          setAssemblyList([]);
          setComponentList([]);
        }
        if (rootType === 'maintenance') setMaintenanceList([]);
        if (rootType === 'amp') setAmpList([]);
        if (rootType === 'adsb') setAdsbList([]);
        if (rootType === 'techlog') setTechlogList([]);
      } finally {
        setEntityOptionsLoading(false);
      }
    };

    fetchData();
  }, [rootType, selectedAircraftId, selectedAssemblyId]);

  const formatAssemblyLabel = (item) =>
    `${item.typeName || 'Assembly'} ${item.serialNumber || ''}`.trim() || item._id;

  const formatComponentLabel = (item) =>
    `${item.partNumber || ''} ${item.code || ''}`.trim() || item._id;

  const formatMaintenanceLabel = (item) =>
    `${item.workOrderNumber || 'WO'} - ${item.title || ''}`.trim() || item._id;

  const formatAmpLabel = (item) =>
    `${item.ampScheduleNumber || ''} ${item.title || ''}`.trim() || item._id;

  const formatAdsbLabel = (item) =>
    `${item.adsbBulletinNumber || ''} ${item.title || ''}`.trim() || item._id;

  const formatTechlogLabel = (item) =>
    `${item.sheetNumber || item.workOrderNumber || ''}`.trim() || item._id;

  const handleIncludeTypeChange = (value, checked) => {
    if (checked) {
      setIncludeTypes((prev) => (prev.includes(value) ? prev : [...prev, value]));
    } else {
      setIncludeTypes((prev) => prev.filter((t) => t !== value));
    }
  };

  const handleLoad = () => {
    if (!canLoad) return;

    onLoad({
      rootType,
      rootId: rootId || undefined,
      asOf: asOfMode === 'date' ? asOfDate : null,
      maxDepth,
      includeTypes: includeTypes.length > 0 ? includeTypes : null,
      onlyWithAnomalies
    });
  };

  const canLoad =
    rootType === 'aircraft'
      ? !!selectedAircraftId
      : rootType === 'assembly'
      ? !!(selectedAircraftId && rootId)
      : rootType === 'component'
      ? !!(selectedAircraftId && selectedAssemblyId && rootId)
      : ['maintenance', 'amp', 'adsb', 'techlog'].includes(rootType)
      ? !!(selectedAircraftId && rootId)
      : rootType === 'charter'
      ? rootId.trim().length > 0
      : false;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountTree fontSize="small" />
        Analytical Tree Controls
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Root Type</InputLabel>
          <Select
            value={rootType}
            onChange={(e) => setRootType(e.target.value)}
            label="Root Type"
          >
            {ROOT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Aircraft selection for hierarchical root types */}
        {['aircraft', 'assembly', 'component', 'maintenance', 'amp', 'adsb', 'techlog'].includes(rootType) && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Aircraft</InputLabel>
            <Select
              value={selectedAircraftId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedAircraftId(val);
                if (rootType === 'aircraft') {
                  setRootId(val || '');
                } else {
                  setSelectedAssemblyId('');
                  setRootId('');
                }
              }}
              label="Aircraft"
            >
              {aircraftList.map((ac) => (
                <MenuItem key={ac._id} value={(ac._id || '').toString()}>
                  {ac.registration || ac._id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Assembly selection when needed */}
        {['assembly', 'component'].includes(rootType) && (
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={!selectedAircraftId || entityOptionsLoading}>
            <InputLabel>Assembly</InputLabel>
            <Select
              value={selectedAssemblyId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedAssemblyId(val);
                if (rootType === 'assembly') {
                  setRootId(val || '');
                } else {
                  setRootId('');
                }
              }}
              label="Assembly"
            >
              {assemblyList.map((a) => (
                <MenuItem key={a._id} value={(a._id || '').toString()}>
                  {formatAssemblyLabel(a)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Component selection when rootType is component */}
        {rootType === 'component' && (
          <FormControl
            size="small"
            sx={{ minWidth: 220 }}
            disabled={!selectedAircraftId || !selectedAssemblyId || entityOptionsLoading}
          >
            <InputLabel>Component</InputLabel>
            <Select
              value={rootId}
              onChange={(e) => setRootId(e.target.value)}
              label="Component"
            >
              {componentList.map((c) => (
                <MenuItem key={c._id} value={(c._id || '').toString()}>
                  {formatComponentLabel(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Entity selection for other aircraft-scoped root types */}
        {rootType === 'maintenance' && (
          <FormControl
            size="small"
            sx={{ minWidth: 260 }}
            disabled={!selectedAircraftId || entityOptionsLoading}
          >
            <InputLabel>Maintenance Work Order</InputLabel>
            <Select
              value={rootId}
              onChange={(e) => setRootId(e.target.value)}
              label="Maintenance Work Order"
            >
              {maintenanceList.map((m) => (
                <MenuItem key={m._id} value={(m._id || '').toString()}>
                  {formatMaintenanceLabel(m)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {rootType === 'amp' && (
          <FormControl
            size="small"
            sx={{ minWidth: 260 }}
            disabled={!selectedAircraftId || entityOptionsLoading}
          >
            <InputLabel>AMP Schedule</InputLabel>
            <Select
              value={rootId}
              onChange={(e) => setRootId(e.target.value)}
              label="AMP Schedule"
            >
              {ampList.map((a) => (
                <MenuItem key={a._id} value={(a._id || '').toString()}>
                  {formatAmpLabel(a)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {rootType === 'adsb' && (
          <FormControl
            size="small"
            sx={{ minWidth: 260 }}
            disabled={!selectedAircraftId || entityOptionsLoading}
          >
            <InputLabel>AD/SB Bulletin</InputLabel>
            <Select
              value={rootId}
              onChange={(e) => setRootId(e.target.value)}
              label="AD/SB Bulletin"
            >
              {adsbList.map((b) => (
                <MenuItem key={b._id} value={(b._id || '').toString()}>
                  {formatAdsbLabel(b)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {rootType === 'techlog' && (
          <FormControl
            size="small"
            sx={{ minWidth: 260 }}
            disabled={!selectedAircraftId || entityOptionsLoading}
          >
            <InputLabel>Tech Log Entry</InputLabel>
            <Select
              value={rootId}
              onChange={(e) => setRootId(e.target.value)}
              label="Tech Log Entry"
            >
              {techlogList.map((t) => (
                <MenuItem key={t._id} value={(t._id || '').toString()}>
                  {formatTechlogLabel(t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Charter remains free text for now */}
        {rootType === 'charter' && (
          <TextField
            size="small"
            label="Entity ID"
            value={rootId}
            onChange={(e) => setRootId(e.target.value)}
            placeholder="ObjectId or identifier"
            sx={{ minWidth: 220 }}
          />
        )}

        <Box>
          <Typography variant="caption" color="text.secondary">As of</Typography>
          <RadioGroup row value={asOfMode} onChange={(e) => setAsOfMode(e.target.value)}>
            <FormControlLabel value="now" control={<Radio size="small" />} label="Now" />
            <FormControlLabel value="date" control={<Radio size="small" />} label="Date" />
          </RadioGroup>
          {asOfMode === 'date' && (
            <TextField
              type="date"
              size="small"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>

        <Box sx={{ minWidth: 120 }}>
          <Typography variant="caption">Max Depth: {maxDepth}</Typography>
          <Slider
            value={maxDepth}
            onChange={(_, v) => setMaxDepth(v)}
            min={1}
            max={5}
            step={1}
            marks
            valueLabelDisplay="auto"
            size="small"
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={onlyWithAnomalies}
              onChange={(e) => setOnlyWithAnomalies(e.target.checked)}
              size="small"
            />
          }
          label="Only nodes with anomalies"
        />

        <Button
          variant="contained"
          onClick={handleLoad}
          disabled={loading || !canLoad || entityOptionsLoading}
          startIcon={loading ? <CircularProgress size={18} /> : null}
        >
          {loading ? 'Loading...' : 'Load Tree'}
        </Button>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">Include types:</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {INCLUDE_TYPES.map((t) => (
            <FormControlLabel
              key={t.value}
              control={
                <Checkbox
                  size="small"
                  checked={includeTypes.includes(t.value)}
                  onChange={(e) => handleIncludeTypeChange(t.value, e.target.checked)}
                />
              }
              label={t.label}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default ControlsPanel;
